import * as d3 from 'd3';
import reactUpdate from 'immutability-helper';

import GapSequenceWrapper, {LineRanges} from 'analyses/alignment/GapSequenceWrapper';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {removeGaps} from 'biojs/Util';
import {
  AlignmentPolymerType,
  ProteinAlignmentKind,
  ProteinAlignment,
  ProteinAlignmentRow,
} from 'constants/AlignmentConstants';
import {getDefaultProteinLayout, ProteinLayout} from 'constants/MapsConstants';
import ProteinAlignmentStore from 'molbio/aa-sequences/AaSequenceAlignmentStore';
import {isFullProteinAlignment} from 'molbio/AlignmentUtil';
import AntibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {PolymerAlignmentTabState} from 'molbio/maps/alignment/PolymerAlignmentTabView';
import linearLayout, {LinearLayout, Line} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import Range from 'molbio/maps/selectables/Range';
import {BasicSelection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import {TabState, TabStateDefaults} from 'Navigation';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface ProteinAlignmentTabStateAttributes extends TabStateDefaults {
  alignment: ProteinAlignment | null;
  proteinId: null | string;
  alignmentId: null | string;
  visibilityOptions: vo.VisibilityOptions;
  summaryMapVisibilityOptions: vo.VisibilityOptions;
  summaryMapLineLayout: Line;
  summaryType: Type;
  range: Range;
  slice: Slice;
  selection: BasicSelection;
  layout: LinearLayout;
  polymerLayout: ProteinLayout;
  localVisibilityOptionsList: Array<vo.VisibilityOptions> | null;
  loaded: boolean;
  bufferViewLayout: undefined;
  scrollData: null;
}

interface ProteinAlignmentTabState {
  get<K extends keyof ProteinAlignmentTabStateAttributes>(key: K): ProteinAlignmentTabStateAttributes[K];
  set<K extends keyof ProteinAlignmentTabStateAttributes>(
    attributeName: K,
    value: ProteinAlignmentTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): ProteinAlignmentTabState;
  set(obj: Partial<ProteinAlignmentTabStateAttributes>): ProteinAlignmentTabState;
}

class ProteinAlignmentTabState extends TabState implements PolymerAlignmentTabState<ProteinAlignmentRow> {
  override defaults(): TabStateDefaults & Partial<ProteinAlignmentTabStateAttributes> {
    const visibilityOptions = [
      {base: vo.PROTEIN_AXIS, visible: true, editable: true, sync: true},
      {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
      {base: vo.LINE, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_COLORING, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_ALIGNMENT_IDENTITY_GRAPH, visible: true, editable: true, sync: true},
      {base: vo.SUMMARY_IDENTITY_GRAPH, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_HYDROPHOBICITY_GRAPH, visible: false, editable: true, sync: false},
      {base: vo.PROTEIN_PI_GRAPH, visible: false, editable: true, sync: false},
      {base: vo.ALIGNMENT_ROW_STATS, visible: true, editable: true, sync: true},
      {base: vo.EXPAND_MINI_MAP, visible: true, editable: true},
      ...(BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
        ? [
            {
              base: vo.ANTIBODY_NUMBERING,
              visible: false,
              editable: true,
              sync: true,
            },
            {
              base: vo.ANTIBODY_CDR_ANNOTATIONS,
              visible: false,
              editable: true,
              sync: true,
            },
            {
              base: vo.PROTEIN_LIABILITY_SITES,
              visible: false,
              editable: true,
              sync: true,
            },
          ]
        : []),
      {base: vo.MAXIMISE, visible: true, editable: true, sync: true},
    ];

    return {
      ...super.defaults(),

      // Required attributes
      proteinId: null,
      alignmentId: null,

      // Configuration
      options: true,

      // Rendering state
      polymerLayout: getDefaultProteinLayout(),

      selection: new BasicSelection({
        polymerLength: 0,
        circular: false,
      }),

      slice: new Slice(),
      range: new Range(),
      scrollData: null,

      // layout / visibility options used across all summary slice views
      // @ts-expect-error
      summaryType: new Type(),

      summaryMapLineLayout: linearLayout().Line,

      summaryMapVisibilityOptions: vo.constructOptions('protein_alignment_summary', [
        {base: vo.MAXIMISE, visible: false, editable: false, sync: false},
        {base: vo.LINE, visible: true, editable: false, sync: false},
      ]),

      // Shared layout options used across all alignment rows.
      layout: linearLayout(),

      // Global / local visibility options
      visibilityOptions: vo.constructOptions('protein_alignment', visibilityOptions),

      // initialized when the alignment is loaded
      localVisibilityOptionsList: null,

      // Synced from store
      title: 'Alignment',

      alignment: null,
    };
  }

  /**
   * Update the tabstate's slice model (backs a SliceView). This method is purely a function of the type and
   * length of the alignment. Ideally, this is not redundantly stored and instead computed on the fly, but
   * existing code relies on having it in this model.
   *
   * This method should be called after any change to type / alignemnt.
   */
  setSliceData(): void {
    if (!this.get('loaded')) {
      return;
    }
    const length = this.getPolymerLength();
    const type = this.getType();
    const start = 0;
    const end = length;
    const width = type.width(length) + type.letterSpacing;

    const x = d3.scaleLinear().clamp(true).domain([start, end]).range([0, width]);
    const xInv = d3.scaleLinear().clamp(true).domain([0, width]).range([start, end]);
    this.get('slice').set({
      start,
      end,
      width,
      x,
      xInv,
      maxWidth: width,
      visibleWidth: width,
      height: 100,
      // @ts-expect-error
      y: 0,
    });
    this.get('range').set({
      getLengthFn() {
        return length;
      },
    });
  }

  setSelection(
    length: number,
    {selectionStart, selectionEnd}: {selectionStart: number; selectionEnd: number | null}
  ): void {
    this.get('selection').set({
      start: selectionStart,
      end: selectionEnd,
      polymerLength: length,
    });
  }

  getType(): Type {
    const {fontSize, letterSpacing} = this.get('polymerLayout').aminoAcids.type;
    return new Type(fontSize, letterSpacing);
  }

  setType({fontSize, letterSpacing}: {fontSize: number; letterSpacing: number}): void {
    this.set({
      polymerLayout: reactUpdate(this.get('polymerLayout'), {
        aminoAcids: {type: {$set: {fontSize, letterSpacing}}},
      }),
    });
    this.setSliceData();
  }

  _getLength(alignment: ProteinAlignment): number {
    return alignment.rows[0].proteinVersion.aminoAcids.length;
  }

  _storeChangeHandler(): void {
    const alignmentState = ProteinAlignmentStore.getAlignmentStateById(this.get('alignmentId')!);
    if (alignmentState) {
      const alignment = alignmentState.alignment;
      assert(isFullProteinAlignment(alignment), 'Expected full protein alignment to be loaded');
      const title = alignment.name;
      const localVisibilityOptionsList = alignment.rows.map((row, i) =>
        this.get('visibilityOptions').makeLocalSync({
          editableOptions: this.get('visibilityOptions')
            .getEditableOptions()
            .map((opt) => opt.get('name'))
            .filter(
              (name) =>
                // We only render the identity graph on the top alignment row view
                (i === 0 || name !== vo.PROTEIN_ALIGNMENT_IDENTITY_GRAPH.name) &&
                // The top alignment row view never has mismatches
                (i !== 0 || name !== vo.LINE.name) &&
                // Only non-template rows on template alignments should show pairwise identity stats
                ((i !== 0 && alignment.alignmentType === ProteinAlignmentKind.TEMPLATE) ||
                  name !== vo.ALIGNMENT_ROW_STATS.name) &&
                // TODO: Amino acids sync with polymerLayout... which are currently shared across all rows,
                // so we don't support toggling it per row view for now.
                name !== vo.AMINO_ACIDS.name &&
                name !== vo.PROTEIN_COLORING.name &&
                // Only relevant to summary map
                name !== vo.SUMMARY_IDENTITY_GRAPH.name
            ),
        })
      );
      const length = this._getLength(alignment);
      this.setSelection(length, {selectionStart: 0, selectionEnd: null});
      this.set({alignment, title, localVisibilityOptionsList, loaded: Boolean(alignment)});
    } else {
      this.set({alignment: null, title: 'Alignment', localVisibilityOptionsList: null, loaded: false});
    }
    // Sync rest of state to store, for TabView to access.
    this.setSliceData();
  }

  _preloadAntibodyEngineeringData(): void {
    if (!BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS) {
      return;
    }

    const alignmentState = ProteinAlignmentStore.getAlignmentStateById(this.get('alignmentId')!);
    if (!alignmentState || !isFullProteinAlignment(alignmentState.alignment)) {
      return;
    }

    const aaSequences = alignmentState.alignment.rows.map((row) => removeGaps(row.proteinVersion.aminoAcids));
    const visibilityOptions = this.get('visibilityOptions');
    if (visibilityOptions.getOption(vo.ANTIBODY_NUMBERING.name)!.get('visible')) {
      const scheme = visibilityOptions
        .getOption(vo.ANTIBODY_NUMBERING.name)!
        .getSetting('numberingScheme')!
        .get('value')
        .toLowerCase();
      AntibodyAnalysisStore.getNumbering(aaSequences, scheme);
    }
    if (visibilityOptions.getOption(vo.ANTIBODY_CDR_ANNOTATIONS.name)!.get('visible')) {
      const definition = visibilityOptions
        .getOption(vo.ANTIBODY_CDR_ANNOTATIONS.name)!
        .getSetting('CDRDefinition')!
        .get('value')
        .toLowerCase();
      AntibodyAnalysisStore.getCDRAnnotations(aaSequences, definition);
    }
  }

  override initialize(options: unknown): void {
    this._storeChangeHandler = this._storeChangeHandler.bind(this);
    this._storeChangeHandler();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ProteinAlignmentStore.addChangeListener(this._storeChangeHandler);
    this.listenTo(this.get('visibilityOptions'), 'redraw', () => {
      this.syncVisibilityOptions();
    });
    this.syncVisibilityOptions();

    // We preload to the AntibodyAnalysisStore here to take advantage of bulk requests
    this._preloadAntibodyEngineeringData();
  }

  // TODO(T8192): Refactor to remove duplication with ProteinLinearMapState
  syncVisibilityOptions(): void {
    const coloringOption = this.get('visibilityOptions').getOption(vo.PROTEIN_COLORING.name)!;
    const summaryIdentityOption = this.get('visibilityOptions').getOption(vo.SUMMARY_IDENTITY_GRAPH.name)!;
    const newPolymerLayout = reactUpdate(this.get('polymerLayout'), {
      aminoAcids: {
        foregroundColoring: {
          $set: coloringOption.getSetting(vo.getProteinColoringSetting().name)!.get('value'),
        },
        showColoring: {$set: coloringOption.get('visible')},
      },
      summaryIdentityGraph: {
        slidingWindowSize: {
          $set: summaryIdentityOption.getSetting(vo.IDENTITY_SLIDING_WINDOW_SETTING.name)!.get('value')!,
        },
      },
    });
    this.set({polymerLayout: newPolymerLayout});
  }

  override cleanUp(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ProteinAlignmentStore.removeChangeListener(this._storeChangeHandler);
  }

  /* The below methods are also implemented by AlignmentTabState, and can thus be used by alignment views */
  getSubmapstates(): Array<ProteinAlignmentRow> {
    return this.get('alignment')!.rows;
  }

  getNameFromSubmapstate(alignmentRow: ProteinAlignmentRow): string {
    return alignmentRow.name;
  }

  getLineRanges(alignmentRow: ProteinAlignmentRow): LineRanges {
    const residues = alignmentRow.proteinVersion.aminoAcids;
    const masterResidues = this.get('alignment')!.rows[0].proteinVersion.aminoAcids;
    return GapSequenceWrapper.computeLineRanges(residues, masterResidues, null, AlignmentPolymerType.AA);
  }

  getCidForSubmapstate(row: ProteinAlignmentRow): number {
    return row.proteinVersion.id;
  }

  getMasterSubmapstateCid(): number {
    return this.getCidForSubmapstate(this.getSubmapstates()[0]);
  }

  /**
   * This returns an instance of Type, which is typically mutable. It is unexpected for any view to call
   * a sideeffectful instance method, since it will have no side effect. The caller should use another
   * tabstate method, e.g. fitSummaryTypeToWidth, which updates the underlying representation of the type.
   * @return {Type}
   */
  getSummaryType(): Type {
    // NB: We can't use a POJO of {fontWidth, letterSpacing}, since alignments rely on fitToWidth being able
    // to call type.setWidth to specify fractional widths.
    return this.get('summaryType');
  }

  fitSummaryTypeToWidth(chars: number, width: number): void {
    const type = this.getSummaryType();
    type.fitToWidth(chars, width, {allowFractionalSpacing: true});
  }

  getSummaryMapLineLayout(): Line {
    return this.get('summaryMapLineLayout');
  }

  getSummaryMapVisibilityOptions(): vo.VisibilityOptions {
    return this.get('summaryMapVisibilityOptions');
  }

  setSummaryMapLineLayout({height, vMargin}: {height: number; vMargin: number}): void {
    this.get('summaryMapLineLayout').set({height, vMargin});
  }

  getPolymerLength(): number {
    return this._getLength(this.get('alignment')!);
  }
}

export default ProteinAlignmentTabState;
